import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Shield, Calendar, User, Mail, CheckCircle2, XCircle, 
  Clock, AlertTriangle, Loader2, Building, Lock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { getInvitationByToken, acceptInvitation } from '@/services/scheduledAudit';
import { authAPI } from '@/services/api';
import { toast } from 'sonner';

const AuditInviteAcceptPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [audit, setAudit] = useState(null);
  const [error, setError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Registration form for external auditors
  const [showRegister, setShowRegister] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    organization: '',
    country: 'US'
  });
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    checkAuthAndLoadInvitation();
  }, [token]);

  const checkAuthAndLoadInvitation = async () => {
    try {
      setLoading(true);
      
      // Check if user is logged in
      const authToken = localStorage.getItem('token');
      if (authToken) {
        try {
          const response = await authAPI.getMe();
          setCurrentUser(response.data);
          setIsLoggedIn(true);
        } catch {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      
      // Load invitation details
      const data = await getInvitationByToken(token);
      setInvitation(data.invitation);
      setAudit(data.audit);
      
      // Pre-fill email for external auditors
      if (data.invitation?.auditor_email) {
        setRegisterForm(prev => ({
          ...prev,
          email: data.invitation.auditor_email,
          fullName: data.invitation.auditor_name || ''
        }));
      }
      
    } catch (err) {
      console.error('Error loading invitation:', err);
      if (err.response?.status === 404) {
        setError('invalid');
      } else if (err.response?.status === 400) {
        setError('expired');
      } else {
        setError('error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!isLoggedIn) {
      toast.error('Please log in or create an account first');
      return;
    }
    
    try {
      setAccepting(true);
      await acceptInvitation(token);
      toast.success('Invitation accepted! You are now part of the audit team.');
      
      // Redirect to the audit review page
      setTimeout(() => {
        navigate(`/grc/audit/scheduled/${invitation.scheduled_audit_id}/review`);
      }, 1500);
    } catch (err) {
      console.error('Error accepting invitation:', err);
      toast.error(err.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (registerForm.password.length < 14) {
      toast.error('Password must be at least 14 characters');
      return;
    }
    
    try {
      setRegistering(true);
      
      // Register the user
      await authAPI.register({
        email: registerForm.email,
        password: registerForm.password,
        confirm_password: registerForm.confirmPassword,
        full_name: registerForm.fullName,
        organization_name: registerForm.organization,
        country: registerForm.country,
        terms_accepted: true
      });
      
      // Login the user
      const loginResponse = await authAPI.login({
        email: registerForm.email,
        password: registerForm.password
      });
      
      localStorage.setItem('token', loginResponse.data.access_token);
      localStorage.setItem('user', JSON.stringify(loginResponse.data.user));
      setCurrentUser(loginResponse.data.user);
      setIsLoggedIn(true);
      setShowRegister(false);
      
      toast.success('Account created successfully! You can now accept the invitation.');
      
    } catch (err) {
      console.error('Registration error:', err);
      toast.error(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setRegistering(false);
    }
  };

  const handleLogin = () => {
    // Store the return URL so user comes back after login
    sessionStorage.setItem('returnUrl', `/audit-invite/${token}`);
    navigate('/login');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getRoleBadge = (role) => {
    const roles = {
      'lead_auditor': { label: 'Lead Auditor', color: 'bg-purple-100 text-purple-700' },
      'auditor': { label: 'Auditor', color: 'bg-blue-100 text-blue-700' },
      'observer': { label: 'Observer', color: 'bg-gray-100 text-gray-700' }
    };
    return roles[role] || { label: role, color: 'bg-gray-100 text-gray-700' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading invitation details...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            {error === 'expired' ? (
              <>
                <Clock className="h-16 w-16 text-orange-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">Invitation Expired</h2>
                <p className="text-gray-600 mb-6">
                  This invitation has expired. Please contact the audit administrator to request a new invitation.
                </p>
              </>
            ) : error === 'invalid' ? (
              <>
                <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">Invalid Invitation</h2>
                <p className="text-gray-600 mb-6">
                  This invitation link is invalid or has already been used.
                </p>
              </>
            ) : (
              <>
                <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">Something Went Wrong</h2>
                <p className="text-gray-600 mb-6">
                  Unable to load the invitation. Please try again later.
                </p>
              </>
            )}
            <Button onClick={() => navigate('/login')} variant="outline">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const roleBadge = getRoleBadge(invitation?.role);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-lg space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Invitation</h1>
          <p className="text-gray-600 mt-1">You've been invited to participate in an audit</p>
        </div>

        {/* Invitation Details Card */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">{audit?.title || 'Scheduled Audit'}</CardTitle>
            <CardDescription>{audit?.framework_name}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Audit Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-gray-500">Scheduled Date</p>
                  <p className="font-medium">{formatDate(audit?.scheduled_date)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-gray-500">Your Role</p>
                  <Badge className={roleBadge.color}>{roleBadge.label}</Badge>
                </div>
              </div>
            </div>

            {/* Invitee Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Mail className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">{invitation?.auditor_name || 'Auditor'}</p>
                  <p className="text-sm text-gray-500">{invitation?.auditor_email}</p>
                </div>
              </div>
            </div>

            {/* Expiration Warning */}
            {invitation?.token_expires_at && (
              <div className="flex items-center gap-2 text-sm text-orange-600 bg-orange-50 p-3 rounded-lg">
                <Clock className="h-4 w-4" />
                <span>Expires on {formatDate(invitation.token_expires_at)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Card */}
        <Card>
          <CardContent className="pt-6">
            {isLoggedIn ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Logged in as</p>
                    <p className="text-sm text-green-600">{currentUser?.email}</p>
                  </div>
                </div>
                
                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={handleAcceptInvitation}
                  disabled={accepting}
                >
                  {accepting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Accepting...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Accept Invitation
                    </>
                  )}
                </Button>
              </div>
            ) : showRegister ? (
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="text-center mb-4">
                  <h3 className="font-semibold">Create Your Account</h3>
                  <p className="text-sm text-gray-500">Fill in your details to join the audit</p>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                      disabled={!!invitation?.auditor_email}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="fullName">Full Name</Label>
                    <Input
                      id="fullName"
                      value={registerForm.fullName}
                      onChange={(e) => setRegisterForm({...registerForm, fullName: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="organization">Organization</Label>
                    <Input
                      id="organization"
                      value={registerForm.organization}
                      onChange={(e) => setRegisterForm({...registerForm, organization: e.target.value})}
                      placeholder="Your company name"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                      placeholder="Min 14 characters"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={registerForm.confirmPassword}
                      onChange={(e) => setRegisterForm({...registerForm, confirmPassword: e.target.value})}
                      required
                    />
                  </div>
                </div>
                
                <div className="pt-2 space-y-2">
                  <Button type="submit" className="w-full" disabled={registering}>
                    {registering ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      'Create Account & Accept'
                    )}
                  </Button>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    className="w-full"
                    onClick={() => setShowRegister(false)}
                  >
                    Back
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <p className="text-center text-gray-600">
                  Please log in or create an account to accept this invitation
                </p>
                
                <Button className="w-full" size="lg" onClick={handleLogin}>
                  <Lock className="h-4 w-4 mr-2" />
                  Log In to Accept
                </Button>
                
                {invitation?.is_external && (
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    size="lg"
                    onClick={() => setShowRegister(true)}
                  >
                    <Building className="h-4 w-4 mr-2" />
                    Create New Account
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500">
          Beskar Security Platform - Enterprise Audit Management
        </p>
      </div>
    </div>
  );
};

export default AuditInviteAcceptPage;
